export const PROPERTIES_COLLECTION = "Properties";

const DEFAULT_PROPERTY_CONFIG = {
  Main: {
    address: "Main Street, Wolfville, Nova Scotia, Canada",
    description:
      "Comfortable apartment living with practical layouts and easy access to the Wolfville area.",
    label: "Main Street Apartments",
    mapQuery: "Main Street, Wolfville, Nova Scotia, Canada",
    propertyStoragePath: "Photos/Main",
    summary: "Manage the homepage/property slideshow for Main Street, then drill into each unit gallery.",
    units: {
      "Unit 1": { label: "Unit 1", legacyId: "Room1", storagePath: "Photos/Main/Unit1" },
      "Unit 2": { label: "Unit 2", legacyId: "Room2", storagePath: "Photos/Main/Unit2" },
      "Unit 3": { label: "Unit 3", legacyId: "Room3", storagePath: "Photos/Main/Unit3" },
      "Unit 4": { label: "Unit 4", legacyId: "Room4", storagePath: "Photos/Main/Unit4" },
      "Unit 5": { label: "Unit 5", legacyId: "Room5", storagePath: "Photos/Main/Unit5" },
      "Unit 6": { label: "Unit 6", legacyId: "Room6", storagePath: "Photos/Main/Unit6" },
      "Unit 7": { label: "Unit 7", legacyId: "Room7", storagePath: "Photos/Main/Unit7" },
      "Unit 8": { label: "Unit 8", legacyId: "Room8", storagePath: "Photos/Main/Unit8" },
    },
  },
  Summer: {
    address: "Summer Street, Wolfville, Nova Scotia, Canada",
    description:
      "Spacious student-friendly suites with a mix of shared and private living options near Wolfville.",
    label: "The Summer Street Suites",
    mapQuery: "Summer Street, Wolfville, Nova Scotia, Canada",
    propertyStoragePath: "Photos/Summer",
    summary: "Manage the Summer Street featured gallery and each unit-specific set of photos.",
    units: {
      "Unit 1": { label: "Unit 1 - 6 Bedroom", legacyId: "Room1", storagePath: "Photos/Summer/Unit1" },
      "Unit 2": { label: "Unit 2 - 6 Bedroom", legacyId: "Room2", storagePath: "Photos/Summer/Unit2" },
      "Unit 3": { label: "Unit 3 - 2 Bedroom", legacyId: "Room3", storagePath: "Photos/Summer/Unit3" },
      "Unit 4": { label: "Unit 4 - 1 Bedroom", legacyId: "Room4", storagePath: "Photos/Summer/Unit4" },
      "Unit 5": { label: "Unit 5 - 1 Bedroom", legacyId: "Room5", storagePath: "Photos/Summer/Unit5" },
      "Unit 6": { label: "Unit 6 - 1 Bedroom", legacyId: "Room6", storagePath: "Photos/Summer/Unit6" },
      "Unit 7": { label: "Unit 7 - 1 Bedroom", legacyId: "Room7", storagePath: "Photos/Summer/Unit7" },
      "Unit 8": { label: "Unit 8 - 2 Bedroom", legacyId: "Room8", storagePath: "Photos/Summer/Unit8" },
    },
  },
};

export function formatTitle(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function normalizeLookupKey(value) {
  return String(value || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
}

function inferUnitNumber(value) {
  const match = String(value || "").match(/\d+/);
  return match?.[0] || "";
}

export function formatDateInputValue(value = new Date()) {
  const nextDate = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(nextDate.getTime())) {
    return "";
  }

  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, "0");
  const day = String(nextDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function normalizeAvailabilityDate(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return formatDateInputValue(parsedDate);
}

export function formatAvailabilityDate(value) {
  const normalizedDate = normalizeAvailabilityDate(value);

  if (!normalizedDate) {
    return "";
  }

  const [year, month, day] = normalizedDate.split("-").map(Number);
  const dateValue = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("en-CA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(dateValue);
}

export function resolveUnitAvailability(unitData = {}, now = new Date()) {
  const availableOn =
    normalizeAvailabilityDate(
      unitData.AvailableOn || unitData.availableOn || unitData.available_date
    );
  const manualAvailable = Boolean(unitData.Available ?? unitData.available);
  const today = formatDateInputValue(now);
  const isScheduledForFuture = Boolean(availableOn && availableOn > today);
  const isScheduledNow = Boolean(availableOn && availableOn <= today);

  if (isScheduledForFuture) {
    return {
      available: false,
      availableOn,
      statusClass: "scheduled",
      statusText: `Available on ${formatAvailabilityDate(availableOn)}`,
    };
  }

  if (manualAvailable || isScheduledNow) {
    return {
      available: true,
      availableOn,
      statusClass: "available",
      statusText: "Available",
    };
  }

  return {
    available: false,
    availableOn,
    statusClass: "unavailable",
    statusText: "Unavailable",
  };
}

export function getDefaultPropertyConfig(propertyId) {
  const lookupKey = normalizeLookupKey(propertyId);

  return (
    Object.entries(DEFAULT_PROPERTY_CONFIG).find(([configKey]) => normalizeLookupKey(configKey) === lookupKey)?.[1] ??
    null
  );
}

export function getDefaultUnitConfig(propertyId, unitId) {
  const propertyConfig = getDefaultPropertyConfig(propertyId);

  if (!propertyConfig) {
    return null;
  }

  const lookupKey = normalizeLookupKey(unitId);

  return (
    Object.entries(propertyConfig.units).find(([configKey]) => normalizeLookupKey(configKey) === lookupKey)?.[1] ??
    null
  );
}

export function createPropertyStoragePath(propertyId) {
  return `Photos/${String(propertyId || "").trim()}`;
}

export function createPropertyDocumentPath(propertyId) {
  return `${PROPERTIES_COLLECTION}/${propertyId}`;
}

export function createUnitsCollectionPath(propertyId) {
  return `${createPropertyDocumentPath(propertyId)}/Units`;
}

export function createUnitDocumentId(unitNumber) {
  return `Unit ${String(unitNumber).trim()}`;
}

export function createUnitStoragePath(propertyStoragePath, unitNumber) {
  return `${propertyStoragePath}/Unit${String(unitNumber).trim()}`;
}

export function createUnitDocumentPath(propertyId, unitId) {
  return `${createUnitsCollectionPath(propertyId)}/${unitId}`;
}

function compareUnits(firstUnit, secondUnit) {
  const firstNumber = Number(inferUnitNumber(firstUnit.id)) || Number.MAX_SAFE_INTEGER;
  const secondNumber = Number(inferUnitNumber(secondUnit.id)) || Number.MAX_SAFE_INTEGER;

  if (firstNumber === secondNumber) {
    return firstUnit.label.localeCompare(secondUnit.label);
  }

  return firstNumber - secondNumber;
}

export function normalizeUnit(propertyId, unitId, unitData = {}) {
  const defaultUnit = getDefaultUnitConfig(propertyId, unitId);
  const unitNumber = inferUnitNumber(unitId || defaultUnit?.legacyId || defaultUnit?.label);
  const propertyDefaults = getDefaultPropertyConfig(propertyId);
  const propertyStoragePath =
    unitData.propertyStoragePath ||
    propertyDefaults?.propertyStoragePath ||
    createPropertyStoragePath(propertyId);
  const availability = resolveUnitAvailability(unitData);

  return {
    available: availability.available,
    availableOn: availability.availableOn,
    availabilityStatusClass: availability.statusClass,
    availabilityStatusText: availability.statusText,
    id: unitId,
    documentPath: createUnitDocumentPath(propertyId, unitId),
    label: unitData.label || defaultUnit?.label || unitId,
    legacyId:
      unitData.legacyId ||
      defaultUnit?.legacyId ||
      (unitNumber ? `Room${unitNumber}` : ""),
    sortOrder: unitData.sortOrder ?? (unitNumber ? Number(unitNumber) : Number.MAX_SAFE_INTEGER),
    storagePath:
      unitData.storagePath ||
      defaultUnit?.storagePath ||
      createUnitStoragePath(propertyStoragePath, unitNumber || unitId),
  };
}

export function normalizeProperty(propertyId, propertyData = {}, unitDocuments = []) {
  const defaultProperty = getDefaultPropertyConfig(propertyId);
  const propertyStoragePath =
    propertyData.propertyStoragePath ||
    propertyData.storagePath ||
    defaultProperty?.propertyStoragePath ||
    createPropertyStoragePath(propertyId);
  const units = unitDocuments
    .map((unitDocument) =>
      normalizeUnit(propertyId, unitDocument.id, {
        ...unitDocument.data,
        propertyStoragePath,
      })
    )
    .sort(compareUnits);

  return {
    address:
      propertyData.address ||
      propertyData.propertyAddress ||
      propertyData.mapQuery ||
      propertyData.mapLocation ||
      propertyData.location ||
      defaultProperty?.address ||
      defaultProperty?.mapQuery ||
      "",
    description:
      propertyData.description ||
      propertyData.publicDescription ||
      propertyData.homepageDescription ||
      defaultProperty?.description ||
      "",
    documentPath: createPropertyDocumentPath(propertyId),
    id: propertyId,
    label: propertyData.label || defaultProperty?.label || formatTitle(propertyId),
    mapQuery:
      propertyData.mapQuery ||
      propertyData.mapLocation ||
      propertyData.address ||
      propertyData.location ||
      defaultProperty?.mapQuery ||
      defaultProperty?.address ||
      "",
    propertyStoragePath,
    summary:
      propertyData.summary ||
      defaultProperty?.summary ||
      `Manage the featured ${formatTitle(propertyId)} gallery, then drill into each unit gallery.`,
    units,
  };
}
